import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Loader2,
  ServerCog,
  XCircle,
} from 'lucide-react';
import { api, ApiError } from '../api/client.js';
import { CredBlock } from '../components/CredBlock.jsx';
import { DnsPreview, DnsOutcome, useDnsPreview } from '../components/DnsPreview.jsx';

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POLL_INTERVAL_MS = 2500;

// Stages: 'idle' → form shown
//         'submitting' → POST in flight
//         'tracking' → got job_id, polling
//         'done' → job.status='done'
//         'failed' → job.status='failed' OR POST returned 4xx/5xx
export function CreateSite() {
  const [stage, setStage] = useState('idle');
  const [form, setForm] = useState({ domain: '', admin_email: '', client_name: '' });
  const [formError, setFormError] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const pollTimerRef = useRef(null);
  // DNS auto: preview the A @ + www records against the live zone; written
  // only on submit, BEFORE the create (certbot needs them), when ticked.
  const [setDns, setSetDns] = useState(true);
  const [dnsNonce, setDnsNonce] = useState(0);
  const [dnsResult, setDnsResult] = useState(null);
  const typedDomain = form.domain.trim().toLowerCase();
  const dns = useDnsPreview(DOMAIN_RE.test(typedDomain)
    ? { path: '/api/sites/dns/preview', body: { domain: typedDomain, kind: 'wp' }, nonce: dnsNonce }
    : null);
  const dnsWillApply = setDns && dns.preview?.can_apply === true;

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => clearPollTimer(), []);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setFormError(null);
  };

  const validate = () => {
    const domain = form.domain.trim().toLowerCase();
    const admin_email = form.admin_email.trim();
    const client_name = form.client_name.trim();

    if (!DOMAIN_RE.test(domain)) return { error: 'Enter a valid domain (e.g. site.example.com)' };
    if (!EMAIL_RE.test(admin_email)) return { error: 'Enter a valid email address' };

    return { data: { domain, admin_email, client_name } };
  };

  const pollJob = useCallback(async (jobId) => {
    try {
      const latest = await api(`/api/sites/jobs/${jobId}`);
      setJob(latest);
      if (latest.status === 'done') {
        setStage('done');
        clearPollTimer();
        return;
      }
      if (latest.status === 'failed') {
        setStage('failed');
        setError(latest.error);
        clearPollTimer();
        return;
      }
      // still pending/running → re-arm
      pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS);
    } catch (err) {
      // If the poll itself fails (network, 404, etc.) keep retrying — transient
      // errors shouldn't wipe the provisioning state. Back off a little.
      pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS * 2);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v.error) {
      setFormError(v.error);
      return;
    }

    setStage('submitting');
    setFormError(null);
    setError(null);
    setDnsResult(null);

    if (dnsWillApply) {
      try {
        const r = await api('/api/sites/dns/apply', {
          method: 'POST',
          body: { domain: v.data.domain, kind: 'wp', fingerprint: dns.preview.fingerprint },
        });
        setDnsResult(r);
      } catch (err) {
        setStage('idle');
        if (err.status === 409) {
          setFormError('The DNS changed since the preview — check the rows again, then create.');
          setDnsNonce((n) => n + 1);
        } else {
          setDnsResult(err.body ?? { ok: false, detail: err.message });
          setFormError('The DNS records were not confirmed (see below). Untick "Set these DNS records" to create the site without them.');
        }
        return;
      }
    }

    try {
      const res = await api('/api/sites', { method: 'POST', body: v.data });
      setJob({ ...res, target: v.data.domain });
      setStage('tracking');
      pollJob(res.job_id);
    } catch (err) {
      setStage('failed');
      if (err instanceof ApiError) {
        setError({
          code: err.body?.error || `http_${err.status}`,
          detail: err.body?.detail || err.message,
        });
      } else {
        setError({ code: 'network_error', detail: err.message });
      }
    }
  };

  const handleReset = () => {
    clearPollTimer();
    setStage('idle');
    setJob(null);
    setError(null);
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/sites"
          className="inline-flex items-center gap-1 text-sm text-t3 hover:text-t1"
        >
          <ArrowLeft className="w-4 h-4" />
          Sites
        </Link>
      </div>

      {(stage === 'idle' || stage === 'submitting') && (
        <FormCard
          form={form}
          onChange={handleChange}
          onSubmit={handleSubmit}
          disabled={stage === 'submitting'}
          error={formError}
          dns={dns}
          setDns={setDns}
          onSetDns={setSetDns}
          dnsResult={dnsResult}
        />
      )}

      {stage === 'tracking' && <TrackingCard job={job} dnsResult={dnsResult} />}
      {stage === 'done' && <SuccessCard job={job} />}
      {stage === 'failed' && <FailureCard error={error} job={job} onRetry={handleReset} />}
    </div>
  );
}

// ─── Form ───────────────────────────────────────────────────────────────────

function FormCard({ form, onChange, onSubmit, disabled, error, dns, setDns, onSetDns, dnsResult }) {
  return (
    <form
      onSubmit={onSubmit}
      className="bg-white rounded-xl border border-bd p-6 max-w-xl"
    >
      <p className="text-sm text-t2 mb-4">
        Provisions a new WordPress site on VPS 1. Takes 60–120 seconds. The
        admin credentials are generated server-side and stored encrypted in
        the panel — you'll see the plaintext once after provisioning.
      </p>

      <p className="mb-5 text-xs text-t3">
        SSL needs the domain to point at this server <em>before</em> the create. Type the
        domain: the DNS box below reads its zone and can set the A records for you.
      </p>

      <Field
        label="Domain"
        placeholder="example.com"
        value={form.domain}
        onChange={onChange('domain')}
        disabled={disabled}
        hint="Its A records (@ and www) must point at this server for SSL to work."
        monospace
      />

      <Field
        label="Admin email"
        type="email"
        placeholder="admin@example.com"
        value={form.admin_email}
        onChange={onChange('admin_email')}
        disabled={disabled}
        hint="Becomes the WP administrator's email address."
      />

      <Field
        label="Client name"
        placeholder="Client or company name"
        value={form.client_name}
        onChange={onChange('client_name')}
        disabled={disabled}
        hint="Optional — shown in the sites list."
      />

      {(dns.loading || dns.preview || dns.error) && (
        <div className="mb-4 rounded-lg border border-bd bg-s-2 px-3 py-3 space-y-2">
          <DnsPreview {...dns} title="DNS for this site" />
          {dns.preview?.can_apply && (
            <label className="flex items-center gap-2 text-xs text-t1">
              <input type="checkbox" checked={setDns} onChange={(e) => onSetDns(e.target.checked)} disabled={disabled} />
              Set these DNS records before creating (ADD / EDIT rows only; CONFLICT rows are left as they are)
            </label>
          )}
          {dnsResult && <DnsOutcome dns={dnsResult} />}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-err-bd bg-err-bg px-3 py-2 text-xs text-err">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="submit"
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-lg bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-deep disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {disabled && <Loader2 className="w-4 h-4 animate-spin" />}
          <ServerCog className="w-4 h-4" />
          Create site
        </button>
        <Link to="/sites" className="text-sm text-t3 hover:text-t2">
          Cancel
        </Link>
      </div>
    </form>
  );
}

function Field({ label, hint, monospace, ...inputProps }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm font-medium text-t1 mb-1">{label}</span>
      <input
        {...inputProps}
        className={[
          'w-full rounded-lg border border-bd-strong px-3 py-2 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue',
          'disabled:bg-s-2 disabled:text-t3',
          monospace ? 'font-mono' : '',
        ].join(' ')}
      />
      {hint && <span className="mt-1 block text-xs text-t3">{hint}</span>}
    </label>
  );
}

// ─── Tracking (pending / running) ───────────────────────────────────────────

function TrackingCard({ job, dnsResult }) {
  return (
    <div className="bg-white rounded-xl border border-bd p-6 max-w-xl">
      <div className="flex items-center gap-3 mb-4">
        <Loader2 className="w-6 h-6 text-t2 animate-spin" />
        <div>
          <h2 className="text-base font-semibold text-t1">Provisioning site…</h2>
          <p className="text-xs text-t3 mt-0.5">
            Target: <code className="font-mono">{job?.target || '—'}</code>
            {' · '}Status: <code className="font-mono">{job?.status}</code>
          </p>
        </div>
      </div>

      {dnsResult && <div className="mb-3"><DnsOutcome dns={dnsResult} /></div>}
      <ol className="text-sm text-t2 space-y-1 ml-2">
        <li>• Docker containers starting (MySQL + WordPress)</li>
        <li>• Installing WordPress core via wp-cli</li>
        <li>• Waiting for Let's Encrypt SSL (if DNS is ready)</li>
        <li>• Storing encrypted credentials in the panel</li>
      </ol>

      <p className="mt-5 text-xs text-t3">
        Keep this page open — the credentials reveal happens once at the end.
        If you navigate away, the site will still be created but you'll need
        to reset the password to access WP admin.
      </p>
    </div>
  );
}

// ─── Success — one-time credentials reveal ─────────────────────────────────

function SuccessCard({ job }) {
  const creds = job?.result?.credentials;
  const domain = job?.result?.domain;

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="bg-white rounded-xl border border-bd p-5">
        <div className="flex items-center gap-2 text-ok">
          <CheckCircle2 className="w-5 h-5" />
          <h2 className="text-base font-semibold">Site created</h2>
        </div>
        <p className="mt-1 text-sm text-t2">
          Credentials have been saved encrypted in the panel — this reveal is
          one-time.
        </p>
        {domain && <ReachabilityProbe domain={domain} />}
      </div>

      {creds && (
        <>
          <CredBlock title="WordPress admin" items={[
            { label: 'Admin URL', value: creds.wp_admin_url, isLink: true },
            { label: 'Username', value: creds.wp_admin_user },
            { label: 'Password', value: creds.wp_admin_pass, sensitive: true },
          ]} />

          <CredBlock title="Database" items={[
            { label: 'Host', value: creds.db_host },
            { label: 'Name', value: creds.db_name },
            { label: 'User', value: creds.db_user },
            { label: 'Password', value: creds.db_pass, sensitive: true },
          ]} />
        </>
      )}

      <div className="flex gap-3">
        <Link
          to="/sites"
          className="inline-flex items-center gap-2 rounded-lg bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-deep"
        >
          Back to sites
        </Link>
        {creds?.wp_admin_url && (
          <a
            href={creds.wp_admin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-bd-strong bg-white px-4 py-2 text-sm font-medium text-t2 hover:bg-s-2"
          >
            Open WP admin
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Small inline probe that fires once per mount — hits the panel's
 * reachability endpoint right after the 201 so the operator sees the real
 * state of the site (DNS + TLS + WP responding), not just "script exited 0".
 * Rendered inside the emerald "Site created" card.
 */
function ReachabilityProbe({ domain }) {
  const [state, setState] = useState({ status: 'probing' });

  useEffect(() => {
    let cancelled = false;
    api(`/api/sites/${encodeURIComponent(domain)}/reachability`)
      .then((data) => { if (!cancelled) setState({ status: 'ok', data }); })
      .catch((err) => {
        if (!cancelled) setState({ status: 'failed', message: err?.message || String(err) });
      });
    return () => { cancelled = true; };
  }, [domain]);

  if (state.status === 'probing') {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-t3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Verifying <code className="font-mono">{domain}</code> is reachable…
      </div>
    );
  }

  if (state.status === 'failed') {
    // Probe itself failed to run (not the site failing) — e.g. panel
    // transient error. Surface but don't alarm.
    return (
      <div className="mt-3 text-xs text-t3">
        Couldn't run reachability probe: {state.message}
      </div>
    );
  }

  const data = state.data;
  if (!data.reachable) {
    return (
      <div className="mt-3 rounded-lg border border-warn-bd bg-warn-bg px-3 py-2 text-xs text-[#8B5206]">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">
              Site not yet reachable · <code className="font-mono font-normal">{data.error}</code>
            </p>
            {data.detail && (
              <p className="mt-0.5 break-all font-mono text-[#8B5206] text-[11px]">{data.detail}</p>
            )}
            <p className="mt-1 text-[#8B5206]">
              DNS may still be propagating or SSL not yet issued. Credentials below
              are valid — retry in a few minutes from the site detail page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const expected = `https://${domain}/`;
  const redirected = data.final_url && data.final_url !== expected;
  const statusBad = data.status >= 400;
  const tone = statusBad || redirected ? 'amber' : 'emerald';

  if (tone === 'emerald') {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-ok">
        <CheckCircle2 className="w-4 h-4" />
        <span>
          <code className="font-mono">{domain}</code> responded{' '}
          <span className="font-mono">{data.status}</span> in {data.elapsed_ms} ms — live.
        </span>
      </div>
    );
  }

  // Amber branch — reachable but redirected / non-2xx
  return (
    <div className="mt-3 rounded-lg border border-warn-bd bg-warn-bg px-3 py-2 text-xs text-[#8B5206]">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">
            Responded <span className="font-mono">{data.status}</span> in {data.elapsed_ms} ms
            {redirected && ' (redirected)'}
          </p>
          {data.final_url && (
            <p className="mt-0.5 break-all font-mono text-[#8B5206] text-[11px]">
              → {data.final_url}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Failure ───────────────────────────────────────────────────────────────

function FailureCard({ error, job, onRetry }) {
  const [showStderr, setShowStderr] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-err-bd p-5 max-w-2xl">
      <div className="flex items-center gap-2 text-err mb-2">
        <AlertTriangle className="w-5 h-5" />
        <h2 className="text-base font-semibold">Provisioning failed</h2>
      </div>

      <dl className="mt-2 text-sm text-t2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-medium">Code</dt>
        <dd className="font-mono">{error?.code || 'unknown'}</dd>
        {error?.upstream_status ? (
          <>
            <dt className="font-medium">Upstream</dt>
            <dd className="font-mono">{error.upstream_status}</dd>
          </>
        ) : null}
        {error?.detail && (
          <>
            <dt className="font-medium">Detail</dt>
            <dd>{error.detail}</dd>
          </>
        )}
      </dl>

      {error?.stderr_tail && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowStderr((s) => !s)}
            className="text-xs text-t3 hover:text-t1"
          >
            {showStderr ? 'Hide' : 'Show'} script output
          </button>
          {showStderr && (
            <pre className="mt-2 text-xs bg-s-2 border border-bd rounded p-2 overflow-auto max-h-60 whitespace-pre-wrap">
              {error.stderr_tail}
            </pre>
          )}
        </div>
      )}

      <div className="mt-5 flex gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 rounded-lg bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-deep"
        >
          Try again
        </button>
        <Link to="/sites" className="text-sm text-t3 hover:text-t2 self-center">
          Back to sites
        </Link>
      </div>

      {job?.id && (
        <p className="mt-4 text-xs text-t3">
          Job id <code className="font-mono">{job.id}</code>
        </p>
      )}
    </div>
  );
}
